import React, { useEffect, useState } from 'react';
import { Popover, Typography, Box } from '@mui/material';
import FilterBody from './FilterBody';
import CloseIcon from '@mui/icons-material/Close';

import { CancelFormActionButton, ActiveButtonBadge, PrimaryFormActionButton, PrimaryIconButton, PrimaryTextButton } from '../TableStyles';

import { changeDateFormat } from '../../../../Helper/DatePicker/DateConverter';
import RecordCountBox from '../../../../Components/RecordCountBox';

import { FilterIconSvg } from '../../Icons/index'
import { useTranslation } from 'react-i18next';
import PrimaryChip from '../../../../Components/PrimaryChip';
import { isCompositeFilterValid } from './filterTypes/CompositeSelectFilter';


const styles = {
	popoverPaper: {
		width: 554,
		borderRadius: '10px',
		backgroundColor: '#FFFFFF',
		boxShadow: '-2px -2px 4px 0px rgba(151, 151, 151, 0.25), 2px 2px 4px 4px rgba(151, 151, 151, 0.25)',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		borderBottom: '1px solid #E0E0E0',
		padding: '12px 16px 12px 12px',
		justifyContent: 'space-between'
	},
	headerText: {
		fontFamily: 'Roboto',
		fontWeight: 700,
		fontSize: '14px',
		lineHeight: '24px',
		color: '#3D4B5A',
		px: 1,
	},
	footer: {
		p: 1,
		borderTop: '1px solid #E0E0E0',
		display: 'flex',
		justifyContent: 'flex-end',
	},
};

const FilterList = ({ selectedFilters, filters, onFilterClick, onRemove, activeFilterKey }) => {
	const MAX_VISIBLE_FILTERS = 4;
	if (!selectedFilters || selectedFilters.length < 1) return null;

	// Group composite filter subfilters together
	const groupedFilters = {};
	const processedSubFilterKeys = new Set();

	// First, process composite filters
	Object.values(filters).forEach((filter) => {
		if (filter?.displayType === 'compositeSelectFilter') {
			const subFilterKeys = filter.subFilters?.map(sf => sf.key) || [];
			const hasAnySelection = subFilterKeys.some(key => selectedFilters[key] && selectedFilters[key].length > 0);

			if (hasAnySelection) {
				// Combine all subfilter values under the main filter key
				groupedFilters[filter.key] = [];
				subFilterKeys.forEach(subKey => {
					if (selectedFilters[subKey] && selectedFilters[subKey].length > 0) {
						groupedFilters[filter.key].push(...selectedFilters[subKey]);
						processedSubFilterKeys.add(subKey);
					}
				});
			}
		}
	});

	// Then, add non-composite filters
	Object.entries(selectedFilters).forEach(([key, values]) => {
		if (!processedSubFilterKeys.has(key) && values && values.length > 0) {
			groupedFilters[key] = values;
		}
	});

	const filterEntries = Object.entries(groupedFilters);
	const visibleFilters = filterEntries.slice(0, MAX_VISIBLE_FILTERS);
	const remainingFilters = filterEntries.slice(MAX_VISIBLE_FILTERS);
	const remainingFilterKeys = remainingFilters.map(([key]) => key);
	const MAX_LENGTH = 28;

	const formatDate = (dateStr) => {
		return changeDateFormat(dateStr)
	};

	const formatDateTime = (dateStr) => {
		return changeDateFormat(dateStr, "DD MMM YYYY hh:mm A");
	};

	const formatFilterValue = (value, filterMeta) => {
		// Handle composite filters - find the subfilter that contains this value
		if (filterMeta?.displayType === "compositeSelectFilter") {
			// Try to parse as JSON (for radio button rangeValue)
			try {
				const parsed = JSON.parse(value);
				// If it's a rangeValue object, find matching option
				if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
					for (const subFilter of filterMeta.subFilters || []) {
						const option = subFilter.staticData?.find((opt) =>
							JSON.stringify(opt.rangeValue) === JSON.stringify(parsed)
						);
						if (option) {
							return option.value;
						}
					}
				}
			} catch {
				// Not JSON, treat as regular ID
				for (const subFilter of filterMeta.subFilters || []) {
					const option = subFilter.staticData?.find((opt) => opt.id === value);
					if (option) {
						return option.value;
					}
				}
			}
			return value;
		}

		let rawValue = filterMeta?.values?.find((option) => option.id === value)?.value || value;

		if (filterMeta?.displayType === "dateCalender") {
			try {
				const dates = JSON.parse(rawValue);
				return `${formatDate(dates.startDate)} - ${formatDate(dates.endDate)}`;
			} catch {
				console.warn("Invalid date format", rawValue);
			}
		}

		if (filterMeta?.displayType === "dateTimeCalender") {
			try {
				const dates = JSON.parse(rawValue);
				return `${formatDateTime(dates.startDate)} - ${formatDateTime(dates.endDate)}`;
			} catch {
				console.warn("Invalid datetime format", rawValue);
			}
		}

		if (filterMeta?.displayType === "range") {
			try {
				const range = JSON.parse(rawValue);
				console.log("range", range);
				return `${range.start} ${range.unitOfMeasurement} - ${range.end} ${range.unitOfMeasurement}`;
			} catch {
				console.warn("Invalid range format", rawValue);
			}
		}

		return rawValue;
	};

	const formatTooltipText = ([key, values]) => {
		const filterMeta = filters[key];
		const label = filterMeta?.label || key;
		const formattedValues = values.map((v) => formatFilterValue(v, filterMeta)).join(", ");
		return `${label}: ${formattedValues}`;
	};

	return (
		<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 0.75, mx: 0.75 }}>
			{visibleFilters.map(([key, values]) => {
				const filterMeta = filters[key];
				const label = filterMeta?.label || key;
				const formattedValues = values.map((v) => formatFilterValue(v, filterMeta)).filter(Boolean);

				// Skip if no valid values
				if (formattedValues.length === 0) return null;

				let displayText = formattedValues[0];
				if (displayText && `${label}: ${displayText}`.length > MAX_LENGTH) {
					displayText = `${displayText.substring(0, MAX_LENGTH - label.length)}...`;
				}

				return (
					<PrimaryChip
						key={key}
						prefixLabel={label}
						label={formattedValues.length > 1 ? `${displayText} +${formattedValues.length - 1}` : displayText}
						onClick={(event) => onFilterClick(event, key)}
						onDelete={(event) => onRemove(event, [key])}
						deleteIcon={<CloseIcon />}
						tooltipTitle={formatTooltipText([key, values])}
					/>
				);
			})}

			{remainingFilterKeys.length > 0 && (
				<PrimaryChip
					key="remaining"
					label={`+${remainingFilters.length}`}
					onClick={(event) => onFilterClick(event, remainingFilterKeys[0])}
					onDelete={(event) => onRemove(event, remainingFilterKeys)}
					deleteIcon={<CloseIcon />}
					tooltipTitle={
						<>
							{remainingFilters.map(formatTooltipText).map((line, index) => (
								<React.Fragment key={index}>
									{line}
									<br />
								</React.Fragment>
							))}
						</>
					}
				/>
			)}
		</Box>
	);
};

export default function TableFilters({
	filters = {},
	selectedFilters = {},
	setSelectedFilters = () => { }
}) {
	const [anchorEl, setAnchorEl] = useState(null);
	const { t } = useTranslation(['common', 'generic_table']);
	const open = Boolean(anchorEl);
	const id = open ? 'filter-popover' : undefined;
	const [newFilters, setNewFilters] = useState(selectedFilters)
	const [activeFilterKey, setActiveFilterKey] = useState(null);

	let newFilterLength = newFilters ? Object.values(newFilters).flat().length : 0
	let selectedFilterLength = selectedFilters ? Object.values(selectedFilters).flat().length : 0

	useEffect(() => {
		setNewFilters(selectedFilters)
	}, [selectedFilters, open])

	const handleClick = (event, key = null) => {
		setAnchorEl(event.currentTarget);
		setActiveFilterKey(key);
	};

	const handleClose = () => {
		setAnchorEl(null);
		setActiveFilterKey(null);
	};

	const onApplyFilters = () => {
		setSelectedFilters(newFilters)
		setActiveFilterKey(null);
		setAnchorEl(null)
	}

	const removeFilter = (event, keys) => {
		event.stopPropagation();
		const updatedFilters = { ...selectedFilters };

		keys.forEach((key) => {
			const filter = filters[key];

			// If it's a composite filter, remove all its subfilter keys
			if (filter?.displayType === 'compositeSelectFilter') {
				filter.subFilters?.forEach((subFilter) => {
					delete updatedFilters[subFilter.key];
				});
			} else {
				delete updatedFilters[key];
			}
		});

		setSelectedFilters(updatedFilters);
	};

	const shouldDisableApply = () => {
		// If no new filters selected
		if (newFilterLength < 1) {
			// But there are previously applied filters, allow Apply to clear them
			if (selectedFilterLength > 0) {
				return false;
			}
			// No new filters and no previously applied filters, disable Apply
			return true;
		}

		// Check if any composite filters have incomplete selections
		// Note: Partial composite filter selections are auto-cleared when switching to other filters
		const hasInvalidCompositeFilter = Object.values(filters).some((filter) => {
			if (filter?.displayType === 'compositeSelectFilter') {
				// Check if any subfilter of this composite filter is selected
				const hasAnySubFilterSelected = filter.subFilters?.some((subFilter) => {
					return newFilters[subFilter.key] && newFilters[subFilter.key].length > 0;
				});

				// If any subfilter is selected, validate the entire composite filter
				// This ensures both checkboxes AND radio buttons are selected
				if (hasAnySubFilterSelected) {
					return !isCompositeFilterValid(filter, newFilters);
				}
			}
			return false;
		});

		// Disable if there's an invalid (partially selected) composite filter
		return hasInvalidCompositeFilter;
	}

	const handleReset = () => {
		setNewFilters({})
	}

	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
			<ActiveButtonBadge
				variant="dot"
				color="secondary"
				overlap="circular"
				invisible={!selectedFilterLength}>
				<PrimaryIconButton onClick={handleClick} size="small" active={open} data-testid="filter-icon-btn">
					<FilterIconSvg />
				</PrimaryIconButton>
			</ActiveButtonBadge>

			<FilterList
				selectedFilters={selectedFilters}
				filters={filters}
				onFilterClick={handleClick}
				onRemove={removeFilter}
				activeFilterKey={activeFilterKey}
			/>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				slotProps={{ paper: { sx: styles.popoverPaper } }}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={styles.header}>
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<FilterIconSvg sx={{ color: '#5E758D' }} />
							<Typography sx={{ mx: 1 }} variant="fw700s14lh24c3D4B5A"> {t('generic_table:filters.title')} </Typography>
							<RecordCountBox fontVariant={"fw600s10lh24c6D72F6"} count={newFilterLength} />
						</Box>

						{newFilterLength > 0 && <PrimaryTextButton onClick={handleReset}
							sx={{ fontWeight: 500 }}> {t('generic_table:filters.reset')}</PrimaryTextButton>
						}
					</Box>

					<FilterBody
						filters={filters}
						newFilters={newFilters}
						setNewFilters={setNewFilters}
						activeFilterKey={activeFilterKey}
					/>
					<Box sx={styles.footer}>
						<CancelFormActionButton onClick={handleClose}> {t('actions.cancel')} </CancelFormActionButton>
						<PrimaryFormActionButton onClick={onApplyFilters} disabled={shouldDisableApply()} >
							{t('actions.apply')}
						</PrimaryFormActionButton>
					</Box>
				</Box>
			</Popover>
		</Box>
	);
}
